import {
  DeleteConfirmation,
  ExportDialog,
  Percent,
  SearchInput,
  ExportDropdown,
  Page,
  BackLink,
  IconButton,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
  RouteLink,
} from '~/components';
import { useApi, useConfirmation, useIntegrations, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { mimeTypes, QueryString } from '~/utils';
import { ActiveStatusSelect } from '../custom-data/components';
import OpportunityStageDrawer from './OpportunityStageDrawer';
import SalesforceIndicator from '../../pipeline/components/SalesforceIndicator';
import HubspotIndicator from '../../pipeline/components/HubspotIndicator';
import opportunityStageCategories from '~/lookups/opportunity-stage-categories';
import { OpportunityPipelineFilter } from '~/components/filters';
import OpportunityStageViewDrawer from './OpportunityStageViewDrawer';
import StageCollapseConfirmation from './StageCollapseConfirmation';

function OpportunityStagesPage() {
  const documentTitle = useDocumentTitle('Opportunity Stages');

  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();
  const integrations = useIntegrations();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ opportunityPipelines: null, q: '', isActive: 'true', size: 1000 });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      q: params.q,
      isActive: params.isActive ?? undefined,
      sort: params.sort,
      opportunityPipelineId: params.opportunityPipelines?.map((pipeline) => pipeline.id),
    }),
    [params],
  );

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).opportunityStages().get(urlSearchParams);
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, urlSearchParams, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [dialog, setDialog] = useState(null);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onClose={resolve}
        onLoad={api.www
          .workspaces(workspace.id)
          .opportunityStages()
          .get(
            { ...params, isActive: params.isActive ?? undefined },
            { headers: { accept: mimeType }, responseType: 'blob' },
          )}
      />
    ));
  };

  const handleEdit = async (opportunityStage) => {
    setDialog(
      <OpportunityStageDrawer
        opportunityStage={opportunityStage}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Opportunity Stages');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  const handleView = async (opportunityStage) => {
    setDialog(
      <OpportunityStageViewDrawer
        opportunityStage={opportunityStage}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Opportunity Stages');
        }}
      />,
    );
  };

  async function handleActiveStatusChange(item, flag) {
    try {
      await api.www.workspaces(workspace.id).opportunityStages(item.id).setActiveStatus(flag);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDelete(stage) {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this Opportunity Stage?</DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .opportunityStages(stage ? stage.id : undefined)
        .delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  const handleMerge = (stage) => {
    confirmation.prompt((resolve) => (
      <StageCollapseConfirmation
        stage={stage}
        onClose={resolve}
        onCollapse={() => {
          fetchData();
          resolve(true);
        }}
      />
    ));
  };

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;
  const integrationEnabled = integrations?.hubspot || integrations?.salesforce;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Opportunity Stages</Page.Title>
          <Page.Description>Opportunity stages are used to track the progress of opportunities.</Page.Description>
        </Page.Info>

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('opportunity_stages.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('opportunity_stages.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="plus" tooltip="New Opportunity Stage" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleFilterChange} />

          <OpportunityPipelineFilter
            name="opportunityPipelines"
            placeholder="Pipeline"
            value={params.opportunityPipelines}
            onChange={handleFilterChange}
          />

          <ActiveStatusSelect value={params.isActive} onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column>Name</ListView.Column>
            <ListView.Column>Description</ListView.Column>
            <ListView.Column>Pipeline</ListView.Column>
            <ListView.Column width="4rem">Category</ListView.Column>
            <ListView.Column align="right">Probability</ListView.Column>
            <ListView.Column align="right">Used In</ListView.Column>
            {integrationEnabled && (
              <ListView.Column width="4rem" align="left">
                {integrations?.salesforce && <SalesforceIndicator variant="grey" />}
                {integrations?.hubspot && <HubspotIndicator variant="grey" />}
              </ListView.Column>
            )}
            <ListViewActions.Column />
          </ListView.Header>
          <ListView.Body>
            {data.map((item) => {
              const {
                id,
                name,
                description,
                statusId,
                probability,
                isActive,
                opportunityPipeline,
                salesforceId,
                hubspotId,
                usedIn,
              } = item;

              const readOnly = salesforceId || hubspotId;

              return (
                <ListView.Row
                  onClick={() => (readOnly ? handleView(item) : handleEdit(item))}
                  key={id}
                  isDisabled={!isActive}>
                  <ListView.Cell>{name}</ListView.Cell>
                  <ListView.Cell>{description}</ListView.Cell>
                  <ListView.Cell>{opportunityPipeline.name}</ListView.Cell>
                  <ListView.Cell>{opportunityStageCategories[statusId].name}</ListView.Cell>
                  <ListView.Cell>
                    <Percent value={probability / 100} minimumFractionDigits={0} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <RouteLink
                      to={`/app/${workspace.key}/pipeline/opportunities?${new QueryString({
                        opportunityStageId: id,
                        opportunityStageCategoryId: 'all',
                      }).toString()}`}>
                      {usedIn}
                    </RouteLink>
                  </ListView.Cell>
                  <ListView.Cell>
                    {salesforceId && <SalesforceIndicator />}
                    {hubspotId && <HubspotIndicator />}
                  </ListView.Cell>

                  <ListViewActions>
                    {readOnly ? (
                      <ListViewActions.View onClick={() => handleView(item)} />
                    ) : (
                      <>
                        <ListViewActions.Edit onClick={() => handleEdit(item)} />
                        <hr />
                        <ListViewMenu>
                          {({ setIsOpen }) => (
                            <>
                              <ListViewMenu.Item onClick={() => handleEdit(item)}>Edit</ListViewMenu.Item>
                              <ListViewMenu.Item
                                onClick={async () => {
                                  await handleActiveStatusChange(item, !isActive);
                                  setIsOpen(false);
                                }}>
                                {isActive ? 'Deactivate' : 'Activate'}
                              </ListViewMenu.Item>
                              <ListViewMenu.Item onClick={() => handleMerge(item)}>Merge</ListViewMenu.Item>
                              <ListViewMenu.DeleteItem
                                tooltip="This item is currently in use."
                                onCheckDependencies={async (workspace) =>
                                  (await workspace.opportunityStages(id).hasDependencies()).data
                                }
                                onClick={() => handleDelete(item)}>
                                Delete
                              </ListViewMenu.DeleteItem>
                            </>
                          )}
                        </ListViewMenu>
                      </>
                    )}
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>
          <ListView.Status total={data.length} label="Opportunity Stage" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}

export default OpportunityStagesPage;
